// @flow

// PinTypeIdentifier is a small pill that displays context-specific information
// about Pins based on their type and state. For example, gif pins will display
// "GIF" and video pins will display duration.

import React from 'react';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import mapProductImagesToCarouselData from 'app/common/react/components/common/Carousel/utils/mapProductImagesToCarouselData';
import millisToMinutesAndSeconds from 'app/common/lib/util/millisToMinutesAndSeconds';
import { Box } from 'gestalt';
import { type Pin } from 'app/common/react/components/PinRep/types';

type Props = {|
  pin: Pin,
  allowProductCarousel?: boolean,
  allowShoppingPriceIndicator?: boolean,
|};

function buildIdentifierContainer(text: string) {
  return (
    <Box
      display="inlineBlock"
      dangerouslySetInlineStyle={{
        __style: {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '18px',
          color: '#111',
          fontSize: '10px',
          fontWeight: '600',
          height: '24px',
          lineHeight: '24px',
          padding: '0 6px',
        },
      }}
      data-test-id="PinTypeIdentifier"
    >
      {text}
    </Box>
  );
}

export default function PinTypeIdentifier({
  pin,
  allowProductCarousel,
  allowShoppingPriceIndicator,
}: Props) {
  const pinTypes = getPinTypes(pin);

  if (pinTypes.isGif) {
    return buildIdentifierContainer('GIF');
  }

  if (pinTypes.isStoryPin) {
    return buildIdentifierContainer('Story');
  }

  if (pinTypes.isVideo) {
    const videos = pin.videos;
    const { duration = 0 } =
      (videos && videos.video_list && videos.video_list[Object.keys(videos.video_list)[0]]) || {};
    return buildIdentifierContainer(millisToMinutesAndSeconds(duration));
  }

  // Prioritize price pill over carousel badge for carousel product pin on shopping surfaces.
  if (pinTypes.isProduct && allowShoppingPriceIndicator) {
    const offerSummary =
      pin.rich_summary &&
      pin.rich_summary.products &&
      pin.rich_summary.products[0] &&
      pin.rich_summary.products[0].offer_summary;
    const price = offerSummary && (offerSummary.price || offerSummary.max_price);
    return price ? buildIdentifierContainer(price) : null;
  }

  // Display carousel identifier on multi-product-image pin if price indicator not allowed.
  if (allowProductCarousel) {
    const productItem =
      pin.product_pin_data && pin.product_pin_data.items && pin.product_pin_data.items[0];
    const carousel_data =
      (productItem &&
        mapProductImagesToCarouselData(
          pin.images,
          productItem,
          pin.carousel_data && pin.carousel_data.index,
        )) ||
      {};
    const { carousel_slots = [], index = 0 } = carousel_data;
    return buildIdentifierContainer(`${index + 1}/${carousel_slots.length}`);
  }

  if (pinTypes.isCarousel) {
    const carousel_data = pin.carousel_data || {};
    const { carousel_slots = [], index = 0 } = carousel_data;
    return buildIdentifierContainer(`${index + 1}/${carousel_slots.length}`);
  }

  return null;
}
